<template>
  <div class="m-auto m-1230">
    <div class="row align-items-center">
      <div class="col-lg-6 d-none d-lg-block">
        <img
          src="@/assets/images/login.jpg"
          class="rounded-3"
          alt="register"
        />
      </div>
      <div class="col-lg-6">
        <div class="mw-480 ms-lg-auto">
          <h3 class="fs-28 mb-2">Registrati</h3>
          <p class="fw-medium fs-16 mb-4">
            Crea il tuo account per calcolare la tua rata
          </p>
          <form @submit.prevent="showVerificationStep ? handleVerification() : handleRegister()" class="auth-form">
            <!-- API Error Message -->
            <div v-if="apiError" class="alert alert-danger" role="alert">
              {{ apiError }}
            </div>
            <!-- Password Mismatch Error -->
            <div v-if="passwordError" class="alert alert-warning" role="alert">
              {{ passwordError }}
            </div>
            <!-- Success Message for verification code sent -->
            <div v-if="verificationSent && showVerificationStep" class="alert alert-success" role="alert">
              Ti abbiamo inviato un codice di verifica all'indirizzo {{ email }}. Inseriscilo qui sotto per completare la registrazione.
            </div>

            <!-- Registration Form Fields (hidden during verification) -->
            <div v-if="!showVerificationStep">
              <div class="row">
                <div class="col-lg-6">
                  <div class="form-floating mb-3">
                    <input
                      type="text"
                      class="form-control"
                      id="registerFirstName"
                      placeholder="Inserisci il tuo nome"
                      v-model="firstName"
                      required
                    />
                    <label for="registerFirstName">Nome</label>
                  </div>
                </div>
                <div class="col-lg-6">
                  <div class="form-floating mb-3">
                    <input
                      type="text"
                      class="form-control"
                      id="registerLastName"
                      placeholder="Inserisci il tuo cognome"
                      v-model="lastName"
                      required
                    />
                    <label for="registerLastName">Cognome</label>
                  </div>
                </div>
              </div>
              <div class="form-floating mb-3">
                <input
                  type="email"
                  class="form-control"
                  id="registerEmail"
                  placeholder="esempio@email.com"
                  v-model="email"
                  required
                />
                <label for="registerEmail">Indirizzo Email</label>
              </div>
              <div class="form-floating mb-3 position-relative">
                <input
                  :type="passwordFieldType"
                  class="form-control"
                  id="registerPassword"
                  placeholder="Inserisci la password"
                  v-model="password"
                  @blur="validatePasswords"
                  required
                />
                <label for="registerPassword">Password</label>
                <button
                  type="button"
                  class="btn border-0 position-absolute top-50 end-0 translate-middle-y d-flex align-items-center justify-content-center"
                  @click="togglePasswordVisibility"
                >
                  <i class="material-symbols-outlined">
                    {{ isPasswordVisible ? 'visibility_off' : 'visibility' }}
                  </i>
                </button>
              </div>
              <div class="form-floating mb-3 position-relative">
                <input
                  :type="passwordConfirmationFieldType"
                  class="form-control"
                  id="registerPasswordConfirmation"
                  placeholder="Conferma la password"
                  v-model="passwordConfirmation"
                  @blur="validatePasswords"
                  required
                />
                <label for="registerPasswordConfirmation">Conferma Password</label>
                <button
                  type="button"
                  class="btn border-0 position-absolute top-50 end-0 translate-middle-y d-flex align-items-center justify-content-center"
                  @click="togglePasswordConfirmationVisibility"
                >
                  <i class="material-symbols-outlined">
                    {{ isPasswordConfirmationVisible ? 'visibility_off' : 'visibility' }}
                  </i>
                </button>
              </div>
            </div>

            <!-- Email Verification Step (shown when verification is needed) -->
            <div v-if="showVerificationStep">
              <div class="form-floating mb-3">
                <input
                  type="email"
                  class="form-control"
                  id="verifyEmail"
                  placeholder="esempio@email.com"
                  v-model="email"
                  disabled
                />
                <label for="verifyEmail">Indirizzo Email</label>
              </div>
              <div class="form-floating mb-3">
                <input
                  type="text"
                  class="form-control"
                  id="verificationCode"
                  placeholder="Inserisci il codice di verifica"
                  v-model="verificationCode"
                  maxlength="6"
                  required
                />
                <label for="verificationCode">Codice di Verifica</label>
              </div>
              <div class="form-group mb-3">
                <button
                  type="button"
                  class="btn btn-link p-0 text-decoration-none"
                  @click="resendCode"
                  :disabled="resendLoading"
                >
                  <div v-if="resendLoading" class="spinner-border spinner-border-sm me-2" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  Non hai ricevuto il codice? Invia di nuovo
                </button>
              </div>
            </div>
            <div class="form-group mb-3">
              <button
                type="submit"
                class="btn btn-primary fw-medium py-2 px-3 w-100"
                :disabled="isLoading || (!showVerificationStep && !!passwordError)"
              >
                <div v-if="isLoading" class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <div v-else class="d-flex align-items-center justify-content-center py-1">
                  <i class="material-symbols-outlined text-white fs-20 me-2">
                    {{ showVerificationStep ? 'verified_user' : 'person_4' }}
                  </i>
                  <span>{{ showVerificationStep ? 'Verifica Email' : 'Registrati' }}</span>
                </div>
              </button>
            </div>

            <div class="form-group">
              <p>
                Confermando la tua email, accetti i nostri
                <a
                  href="https://smartrate.it/termini-e-condizioni-generali-di-servizio"
                  target="_blank"
                  class="fw-medium text-decoration-none"
                >
                  Termini di Servizio
                </a>
                e di aver letto e compreso la nostra
                <a
                  href="https://smartrate.it/privacy-policy"
                  target="_blank"
                  class="fw-medium text-decoration-none"
                >
                  Informativa Privacy</a>
              </p>
              <p>
                Hai già un account?
                <a
                  href="#"
                  class="fw-medium text-primary text-decoration-none"
                  @click.prevent="$emit('switch-to-login')"
                >
                  Accedi
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { authStore } from "@/stores/auth";
import { formatErrorMessage } from "@/utils/errorUtils";

export default defineComponent({
  name: "AuthRegister",
  emits: ['register-success', 'switch-to-login'],
  setup(props, { emit }) {
    const firstName = ref('');
    const lastName = ref('');
    const email = ref('');
    const password = ref('');
    const passwordConfirmation = ref('');
    const passwordError = ref('');
    const apiError = ref('');
    const isLoading = ref(false);
    const showVerificationStep = ref(false);
    const verificationSent = ref(false);
    const verificationCode = ref('');
    const resendLoading = ref(false);

    const isPasswordVisible = ref(false);
    const passwordFieldType = ref('password');
    const isPasswordConfirmationVisible = ref(false);
    const passwordConfirmationFieldType = ref('password');

    const togglePasswordVisibility = () => {
      isPasswordVisible.value = !isPasswordVisible.value;
      passwordFieldType.value = isPasswordVisible.value ? 'text' : 'password';
    };

    const togglePasswordConfirmationVisibility = () => {
      isPasswordConfirmationVisible.value = !isPasswordConfirmationVisible.value;
      passwordConfirmationFieldType.value = isPasswordConfirmationVisible.value ? 'text' : 'password';
    };

    const validatePasswords = () => {
      if (password.value && passwordConfirmation.value && password.value !== passwordConfirmation.value) {
        passwordError.value = 'Le password non coincidono.';
        return false;
      } else {
        passwordError.value = '';
        return true;
      }
    };

    const handleRegister = async () => {
      apiError.value = '';
      if (!validatePasswords()) {
        return;
      }

      isLoading.value = true;

      try {
        const result = await authStore.register({
          name: firstName.value,
          surname: lastName.value,
          email: email.value,
          password: password.value,
          password_confirmation: passwordConfirmation.value,
          profession: 'Default', // Or get this from a form field if you add one
        });

        if (result.needsVerification) {
          showVerificationStep.value = true;
          // Send verification code automatically when user needs verification
          try {
            await authStore.resendVerification(email.value);
            verificationSent.value = true; // Set only if successful
          } catch (resendError) {
            console.error("Failed to send verification code:", resendError);
            apiError.value = formatErrorMessage(resendError);
            verificationSent.value = false;
          }
        } else {
          emit('register-success');
        }
      } catch (error: any) {
        console.error("Registration failed:", error);
        apiError.value = formatErrorMessage(error);
      } finally {
        isLoading.value = false;
      }
    };

    const handleVerification = async () => {
      apiError.value = '';
      if (!verificationCode.value) {
        apiError.value = 'Inserisci il codice di verifica.';
        return;
      }

      isLoading.value = true;

      try {
        await authStore.verifyEmail({
          email: email.value,
          verification_code: verificationCode.value
        });
        emit('register-success');
      } catch (error: any) {
        console.error("Email verification failed:", error);
        apiError.value = formatErrorMessage(error);
      } finally {
        isLoading.value = false;
      }
    };

    const resendCode = async () => {
      resendLoading.value = true;
      apiError.value = '';

      try {
        await authStore.resendVerification(email.value);
        verificationSent.value = true;
        apiError.value = '';
        // Could show a success message here
      } catch (error: any) {
        verificationSent.value = false;
        console.error("Resend verification failed:", error);
        apiError.value = formatErrorMessage(error);
      } finally {
        resendLoading.value = false;
      }
    };


    return {
      firstName,
      lastName,
      email,
      password,
      passwordConfirmation,
      passwordError,
      apiError,
      isLoading,
      showVerificationStep,
      verificationSent,
      verificationCode,
      resendLoading,
      isPasswordVisible,
      passwordFieldType,
      togglePasswordVisibility,
      isPasswordConfirmationVisible,
      passwordConfirmationFieldType,
      togglePasswordConfirmationVisibility,
      handleRegister,
      handleVerification,
      resendCode,
      validatePasswords,
    };
  },
});
</script>
